import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import api, { dataAPI } from '../lib/api';
import {
  User, Briefcase, GraduationCap, Award, Code2, MapPin, Mail, Phone,
  Linkedin, Building2, Calendar, Star, Plus, X, Zap, Save, Loader2,
  Edit3, Check, Globe, Target, Trash2, Upload, FileText, ArrowRight, Sparkles
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

export default function ProfilePage() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [skills, setSkills] = useState([]);
  const [newSkillName, setNewSkillName] = useState('');
  const [newSkillLevel, setNewSkillLevel] = useState('moderate');
  const [addingSkill, setAddingSkill] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);
  // Editable fields
  const [form, setForm] = useState({});

  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [profileRes, skillsRes] = await Promise.all([
          dataAPI.profile(), dataAPI.skills()
        ]);
        setProfile(profileRes.data);
        setForm(profileRes.data);
        setSkills(skillsRes.data);
      } catch (err) {
        console.error(err);
        setError(err.response?.status === 404 ? 'not_found' : 'error');
        // Set defaults so the page is still usable
        const defaults = { name: '', email: '', title: '', phone: '', location: '', target_location: '', linkedin: '', website: '', bio: '', experience_years: 0, current_company: '', experience: [], education: [], domain_expertise: [] };
        setProfile(defaults);
        setForm(defaults);
      }
      finally { setLoading(false); }
    };
    load();
  }, []);

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10MB'); return; }
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('Only PDF files are supported'); return; }

    setUploading(true);
    setUploadProgress(10);
    try {
      const formData = new FormData();
      formData.append('file', file);

      setUploadProgress(30);
      const res = await api.post('/onboarding/resume', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (p) => setUploadProgress(Math.max(30, Math.round((p.loaded / p.total) * 60))),
      });

      setUploadProgress(80);
      const parsed = res.data?.parsed_data;
      if (parsed) {
        // Auto-fill profile from parsed resume
        const updated = {
          ...form,
          name: parsed.name || form.name,
          title: parsed.current_title || form.title,
          bio: parsed.summary || form.bio,
          experience_years: parsed.years_experience || form.experience_years,
          current_company: parsed.current_company || form.current_company,
          location: parsed.location || form.location,
          experience: parsed.experience?.length ? parsed.experience : form.experience,
          education: parsed.education?.length ? parsed.education : form.education,
        };
        setForm(updated);
        setEditing(true);

        // Auto-add parsed skills
        if (parsed.skills?.length) {
          for (const sk of parsed.skills.slice(0, 15)) {
            try {
              await dataAPI.addSkill({ name: sk, level: 'moderate' });
            } catch {} // duplicate skill is fine
          }
          const skillsRes = await dataAPI.skills();
          setSkills(skillsRes.data);
        }

        toast.success('Resume parsed! Review the extracted data and save.');
      }
      setUploadProgress(100);
    } catch (err) {
      console.error('Resume upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to parse resume. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      const res = await dataAPI.updateProfile({
        name: form.name, title: form.title, phone: form.phone,
        location: form.location, target_location: form.target_location,
        linkedin: form.linkedin, website: form.website, bio: form.bio,
        experience_years: parseInt(form.experience_years) || 0,
        current_company: form.current_company,
        experience: form.experience || [],
        education: form.education || [],
        domain_expertise: form.domain_expertise || [],
      });
      setProfile(res.data);
      setForm(res.data);
      setEditing(false);
      toast.success('Profile saved');
    } catch { toast.error('Failed to save profile'); }
    finally { setSaving(false); }
  };

  const addSkill = async () => {
    if (!newSkillName.trim()) return;
    setAddingSkill(true);
    try {
      const res = await dataAPI.addSkill(newSkillName.trim(), newSkillLevel);
      setSkills(res.data);
      setNewSkillName('');
      toast.success(`Skill "${newSkillName}" added`);
    } catch { toast.error('Failed to add skill'); }
    finally { setAddingSkill(false); }
  };

  const removeSkill = async (name) => {
    try {
      const res = await dataAPI.deleteSkill(name);
      setSkills(res.data);
      toast.success(`Removed "${name}"`);
    } catch { toast.error('Failed to remove skill'); }
  };

  const updateSkillLevel = async (name, level) => {
    try {
      const res = await dataAPI.addSkill(name, level);
      setSkills(res.data);
    } catch { toast.error('Failed to update skill'); }
  };

  // Experience helpers
  const addExperience = () => {
    setForm(prev => ({
      ...prev,
      experience: [...(prev.experience || []), { company: '', role: '', duration: '', focus: '', achievements: [''] }]
    }));
  };
  const removeExperience = (i) => setForm(prev => ({ ...prev, experience: prev.experience.filter((_, idx) => idx !== i) }));
  const updateExp = (i, key, val) => setForm(prev => {
    const exp = [...(prev.experience || [])];
    exp[i] = { ...exp[i], [key]: val };
    return { ...prev, experience: exp };
  });
  const updateAchievement = (ei, ai, val) => setForm(prev => {
    const exp = [...(prev.experience || [])];
    const achs = [...(exp[ei].achievements || [])];
    achs[ai] = val;
    exp[ei] = { ...exp[ei], achievements: achs };
    return { ...prev, experience: exp };
  });
  const addAchievement = (ei) => setForm(prev => {
    const exp = [...(prev.experience || [])];
    exp[ei] = { ...exp[ei], achievements: [...(exp[ei].achievements || []), ''] };
    return { ...prev, experience: exp };
  });
  const removeAchievement = (ei, ai) => setForm(prev => {
    const exp = [...(prev.experience || [])];
    exp[ei] = { ...exp[ei], achievements: exp[ei].achievements.filter((_, i) => i !== ai) };
    return { ...prev, experience: exp };
  });

  // Education helpers
  const addEducation = () => setForm(prev => ({ ...prev, education: [...(prev.education || []), { degree: '', institution: '' }] }));
  const removeEducation = (i) => setForm(prev => ({ ...prev, education: prev.education.filter((_, idx) => idx !== i) }));
  const updateEdu = (i, key, val) => setForm(prev => {
    const edu = [...(prev.education || [])];
    edu[i] = { ...edu[i], [key]: val };
    return { ...prev, education: edu };
  });

  // Domain helpers
  const [newDomain, setNewDomain] = useState('');
  const addDomain = () => {
    if (!newDomain.trim()) return;
    setForm(prev => ({ ...prev, domain_expertise: [...(prev.domain_expertise || []), newDomain.trim()] }));
    setNewDomain('');
  };
  const removeDomain = (i) => setForm(prev => ({ ...prev, domain_expertise: prev.domain_expertise.filter((_, idx) => idx !== i) }));

  const levelStyles = {
    proficient: { bg: 'rgba(56,161,105,0.1)', color: '#38A169', border: 'rgba(56,161,105,0.2)' },
    moderate: { bg: 'rgba(13,148,136,0.1)', color: '#0D9488', border: 'rgba(13,148,136,0.2)' },
    beginner: { bg: 'rgba(49,130,206,0.1)', color: '#3182CE', border: 'rgba(49,130,206,0.2)' },
  };

  if (loading) {
    return (<div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: '#0D9488', borderTopColor: 'transparent' }} /></div>);
  }

  const p = editing ? form : profile;

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="nx-overline mb-1" style={{ color: '#0D9488' }}>Account</p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight leading-none" style={{ color: 'var(--t-text-heading)' }}>My Profile</h1>
        </div>
        <div className="flex gap-2">
          {editing ? (
            <>
              <Button data-testid="cancel-edit-btn" onClick={() => { setEditing(false); setForm(profile); }}
                className="h-8 px-3 text-[10px] font-bold rounded-lg"
                style={{ background: 'var(--t-bg-section)', color: 'var(--t-text-muted)', border: '1px solid var(--t-border)' }}>
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
              <Button data-testid="save-profile-btn" onClick={saveProfile} disabled={saving}
                className="h-8 px-3 text-[10px] font-bold rounded-lg"
                style={{ background: '#38A169', color: '#fff' }}>
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
              </Button>
            </>
          ) : (
            <Button data-testid="edit-profile-btn" onClick={() => setEditing(true)}
              className="h-8 px-3 text-[10px] font-bold rounded-lg"
              style={{ background: '#0D9488', color: '#fff' }}>
              <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit Profile
            </Button>
          )}
        </div>
      </div>

      {/* Profile Hero */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="nx-card p-6" data-testid="profile-hero">
        <div className="flex flex-col md:flex-row items-start gap-6">
          <div className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'rgba(245,132,31,0.12)' }}>
            <span className="text-3xl font-extrabold" style={{ color: '#0D9488' }}>
              {(p.name || 'U').split(' ').map(n => n[0]).join('').slice(0, 2)}
            </span>
          </div>
          <div className="flex-1 space-y-3 w-full">
            {editing ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <PField label="Full Name" testId="edit-name" value={form.name} onChange={v => updateForm('name', v)} />
                <PField label="Professional Title" testId="edit-title" value={form.title} onChange={v => updateForm('title', v)} />
                <PField label="Current Company" testId="edit-company" value={form.current_company} onChange={v => updateForm('current_company', v)} />
                <PField label="Years of Experience" testId="edit-years" value={form.experience_years} onChange={v => updateForm('experience_years', v)} type="number" />
                <PField label="Phone" testId="edit-phone" value={form.phone} onChange={v => updateForm('phone', v)} icon={Phone} />
                <PField label="Location" testId="edit-location" value={form.location} onChange={v => updateForm('location', v)} icon={MapPin} />
                <PField label="Target Location" testId="edit-target" value={form.target_location} onChange={v => updateForm('target_location', v)} icon={Target} />
                <PField label="LinkedIn" testId="edit-linkedin" value={form.linkedin} onChange={v => updateForm('linkedin', v)} icon={Linkedin} />
                <PField label="Website" testId="edit-website" value={form.website} onChange={v => updateForm('website', v)} icon={Globe} />
                <div className="md:col-span-2">
                  <label className="text-[10px] font-bold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Bio</label>
                  <Textarea data-testid="edit-bio" value={form.bio || ''} onChange={e => updateForm('bio', e.target.value)}
                    placeholder="Tell us about yourself..."
                    className="min-h-[60px] resize-none text-sm"
                    style={{ background: 'var(--t-bg-input)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }} />
                </div>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-extrabold" style={{ color: 'var(--t-text-heading)' }}>
                  {p.name || <span style={{ color: 'var(--t-text-muted)', fontWeight: 400, fontSize: '16px' }}>Click "Edit Profile" to add your name</span>}
                </h2>
                {p.title && <p className="text-base font-medium" style={{ color: '#0D9488' }}>{p.title}</p>}
                {p.current_company && <p className="text-sm" style={{ color: 'var(--t-text-muted)' }}>{p.current_company}{p.experience_years ? ` · ${p.experience_years} years exp.` : ''}</p>}
                {p.bio && <p className="text-sm" style={{ color: 'var(--t-text-secondary)' }}>{p.bio}</p>}
                <div className="flex flex-wrap items-center gap-4 mt-2">
                  {p.location && <InfoBit icon={MapPin} text={p.location} />}
                  {p.email && <InfoBit icon={Mail} text={p.email} />}
                  {p.phone && <InfoBit icon={Phone} text={p.phone} />}
                  {p.linkedin && <InfoBit icon={Linkedin} text={p.linkedin} />}
                  {p.website && <InfoBit icon={Globe} text={p.website} />}
                </div>
                {p.target_location && (
                  <div className="flex items-center gap-2 mt-2">
                    <Target className="w-3.5 h-3.5" style={{ color: '#38A169' }} />
                    <span className="text-sm font-semibold" style={{ color: '#38A169' }}>Target: {p.target_location}</span>
                  </div>
                )}
                {p.subscription_plan && p.subscription_plan !== 'free' && (
                  <Badge className="text-[9px] mt-1" style={{ background: '#0D948815', color: '#0D9488', border: '1px solid #0D948830' }}>
                    {p.subscription_plan.charAt(0).toUpperCase() + p.subscription_plan.slice(1)} Plan
                  </Badge>
                )}
              </>
            )}
          </div>
        </div>
      </motion.div>

      {/* Resume Upload Section */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
        className="nx-card p-6" data-testid="resume-upload-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,132,31,0.12)' }}>
            <FileText className="w-4 h-4" style={{ color: '#F5841F' }} />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Resume Upload</h3>
            <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>Upload your resume to auto-fill your profile, skills, and experience</p>
          </div>
          {profile?.resume_path && (
            <Badge className="text-[10px]" style={{ background: 'rgba(56,161,105,0.1)', color: '#38A169', border: '1px solid rgba(56,161,105,0.2)' }}>
              <Check className="w-3 h-3 mr-1" /> Uploaded
            </Badge>
          )}
        </div>
        <input ref={fileInputRef} type="file" accept=".pdf" className="hidden" onChange={handleResumeUpload} data-testid="resume-file-input" />
        {uploading ? (
          <div className="space-y-3 p-4 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border-subtle)' }}>
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#0D9488' }} />
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: 'var(--t-text-heading)' }}>
                  {uploadProgress < 30 ? 'Uploading...' : uploadProgress < 80 ? 'Extracting skills and experience...' : 'Finalizing...'}
                </p>
                <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>This may take a few seconds</p>
              </div>
            </div>
            <Progress value={uploadProgress} className="h-1.5" />
          </div>
        ) : (
          <button onClick={() => fileInputRef.current?.click()} data-testid="upload-resume-btn"
            className="w-full p-6 rounded-lg border-2 border-dashed text-center transition-all hover:border-solid group"
            style={{ borderColor: 'var(--t-border)', background: 'var(--t-bg-section)' }}>
            <Upload className="w-8 h-8 mx-auto mb-2 transition-transform group-hover:scale-110" style={{ color: 'var(--t-text-muted)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--t-text-heading)' }}>
              {profile?.resume_path ? 'Re-upload Resume' : 'Upload Your Resume'}
            </p>
            <p className="text-xs mt-1" style={{ color: 'var(--t-text-muted)' }}>
              PDF only, up to 10MB. We'll auto-extract your skills, experience, and education.
            </p>
          </button>
        )}
      </motion.div>

      {/* Skills Management */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="nx-card p-6" data-testid="skills-management-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(128,90,213,0.12)' }}>
              <Zap className="w-4 h-4" style={{ color: '#805AD5' }} />
            </div>
            <div>
              <h3 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Skills</h3>
              <p className="text-xs" style={{ color: 'var(--t-text-muted)' }}>Add skills to improve job fit scoring</p>
            </div>
          </div>
          <span className="text-xs font-mono" style={{ color: 'var(--t-text-muted)' }}>{skills.length} skills</span>
        </div>
        <div className="flex items-end gap-3 mb-4 p-3 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border-subtle)' }}>
          <div className="flex-1">
            <label className="text-[10px] font-bold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Skill Name</label>
            <Input data-testid="new-skill-input" value={newSkillName} onChange={(e) => setNewSkillName(e.target.value)}
              placeholder="e.g., Python, React, Leadership..."
              className="h-9 text-sm" style={{ background: 'var(--t-bg-input)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
              onKeyDown={(e) => { if (e.key === 'Enter') addSkill(); }} />
          </div>
          <div className="w-36">
            <label className="text-[10px] font-bold mb-1 block" style={{ color: 'var(--t-text-muted)' }}>Level</label>
            <Select value={newSkillLevel} onValueChange={setNewSkillLevel}>
              <SelectTrigger data-testid="skill-level-select" className="h-9" style={{ background: 'var(--t-bg-input)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent style={{ background: 'var(--t-bg-card-solid)', border: '1px solid var(--t-border)' }}>
                <SelectItem value="beginner" style={{ color: '#3182CE' }}>Beginner</SelectItem>
                <SelectItem value="moderate" style={{ color: '#0D9488' }}>Moderate</SelectItem>
                <SelectItem value="proficient" style={{ color: '#38A169' }}>Proficient</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="add-skill-button" onClick={addSkill} disabled={addingSkill || !newSkillName.trim()}
            className="h-9 px-3 text-xs font-bold rounded-lg" style={{ background: '#0D9488', color: '#fff' }}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>
        {skills.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <AnimatePresence>
              {skills.map((skill, i) => {
                const ls = levelStyles[skill.level] || levelStyles.moderate;
                return (
                  <motion.div key={skill.name} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} layout
                    className="flex items-center justify-between p-2.5 rounded-lg" style={{ background: ls.bg, border: `1px solid ${ls.border}` }}
                    data-testid={`managed-skill-${i}`}>
                    <span className="text-sm font-medium" style={{ color: 'var(--t-text-heading)' }}>{skill.name}</span>
                    <div className="flex items-center gap-1.5">
                      <Select value={skill.level} onValueChange={(val) => updateSkillLevel(skill.name, val)}>
                        <SelectTrigger className="bg-transparent border-0 text-[10px] w-24 h-6 p-1" style={{ color: ls.color }}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent style={{ background: 'var(--t-bg-card-solid)', border: '1px solid var(--t-border)' }}>
                          <SelectItem value="beginner" style={{ color: '#3182CE' }}>Beginner</SelectItem>
                          <SelectItem value="moderate" style={{ color: '#0D9488' }}>Moderate</SelectItem>
                          <SelectItem value="proficient" style={{ color: '#38A169' }}>Proficient</SelectItem>
                        </SelectContent>
                      </Select>
                      <button data-testid={`remove-skill-${i}`} onClick={() => removeSkill(skill.name)} className="p-0.5 rounded" style={{ color: 'var(--t-text-muted)' }}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : (
          <div className="text-center py-6" style={{ color: 'var(--t-text-muted)' }}>
            <Zap className="w-6 h-6 mx-auto mb-1.5 opacity-20" />
            <p className="text-xs">Add skills to improve job matching accuracy</p>
          </div>
        )}
      </motion.div>

      {/* Experience */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="nx-card p-6" data-testid="experience-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(49,130,206,0.12)' }}>
              <Briefcase className="w-4 h-4" style={{ color: '#3182CE' }} />
            </div>
            <h3 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Experience</h3>
          </div>
          {editing && (
            <Button onClick={addExperience} className="h-7 px-2 text-[10px] font-bold rounded" data-testid="add-experience-btn"
              style={{ background: '#3182CE15', color: '#3182CE', border: '1px solid #3182CE30' }}>
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          )}
        </div>
        {(p.experience || []).length > 0 ? (
          <div className="space-y-4">
            {(p.experience || []).map((exp, i) => (
              <div key={i} data-testid={`experience-${i}`} className="p-4 rounded-lg" style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border-subtle)' }}>
                {editing ? (
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <div className="grid grid-cols-2 gap-2 flex-1 mr-2">
                        <PField label="Role" testId={`exp-role-${i}`} value={exp.role} onChange={v => updateExp(i, 'role', v)} />
                        <PField label="Company" testId={`exp-company-${i}`} value={exp.company} onChange={v => updateExp(i, 'company', v)} />
                        <PField label="Duration" testId={`exp-duration-${i}`} value={exp.duration} onChange={v => updateExp(i, 'duration', v)} />
                        <PField label="Focus" testId={`exp-focus-${i}`} value={exp.focus} onChange={v => updateExp(i, 'focus', v)} />
                      </div>
                      <button onClick={() => removeExperience(i)} className="p-1 text-red-400 h-fit"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold" style={{ color: 'var(--t-text-muted)' }}>Achievements</label>
                      {(exp.achievements || []).map((a, ai) => (
                        <div key={ai} className="flex items-center gap-1.5">
                          <span className="text-[10px]" style={{ color: 'var(--t-text-muted)' }}>-</span>
                          <Input value={a} onChange={e => updateAchievement(i, ai, e.target.value)} className="flex-1 h-7 text-[11px]"
                            style={{ background: 'var(--t-bg-input)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }} />
                          <button onClick={() => removeAchievement(i, ai)} className="p-0.5 text-red-400/50"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <button onClick={() => addAchievement(i)} className="text-[10px] flex items-center gap-1 px-1.5 py-0.5" style={{ color: '#3182CE' }}>
                        <Plus className="w-3 h-3" /> Achievement
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: 'var(--t-bg-input)', border: '1px solid var(--t-border)' }}>
                      <Building2 className="w-4 h-4" style={{ color: 'var(--t-text-muted)' }} />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>{exp.role}</h4>
                      <p className="text-xs" style={{ color: '#0D9488' }}>{exp.company}</p>
                      <p className="text-[10px] mt-0.5" style={{ color: 'var(--t-text-muted)' }}>{exp.duration}{exp.focus ? ` · ${exp.focus}` : ''}</p>
                      {exp.achievements?.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {exp.achievements.filter(Boolean).map((a, j) => (
                            <li key={j} className="text-xs flex items-start gap-1.5" style={{ color: 'var(--t-text-secondary)' }}>
                              <Check className="w-3 h-3 flex-shrink-0 mt-0.5" style={{ color: '#38A169' }} /> {a}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6" style={{ color: 'var(--t-text-muted)' }}>
            <Briefcase className="w-6 h-6 mx-auto mb-1.5 opacity-20" />
            <p className="text-xs">{editing ? 'Click "Add" to add your work experience' : 'No experience added yet. Click "Edit Profile" to add.'}</p>
          </div>
        )}
      </motion.div>

      {/* Education */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
        className="nx-card p-6" data-testid="education-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(128,90,213,0.12)' }}>
              <GraduationCap className="w-4 h-4" style={{ color: '#805AD5' }} />
            </div>
            <h3 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Education</h3>
          </div>
          {editing && (
            <Button onClick={addEducation} className="h-7 px-2 text-[10px] font-bold rounded" data-testid="add-education-btn"
              style={{ background: '#805AD515', color: '#805AD5', border: '1px solid #805AD530' }}>
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          )}
        </div>
        {(p.education || []).length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(p.education || []).map((edu, i) => (
              <div key={i} data-testid={`education-${i}`} className="p-3 rounded-lg"
                style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border-subtle)' }}>
                {editing ? (
                  <div className="space-y-1.5">
                    <div className="flex justify-between">
                      <div className="flex-1 mr-2 space-y-1.5">
                        <PField label="Degree" testId={`edu-degree-${i}`} value={edu.degree} onChange={v => updateEdu(i, 'degree', v)} />
                        <PField label="Institution" testId={`edu-inst-${i}`} value={edu.institution} onChange={v => updateEdu(i, 'institution', v)} />
                      </div>
                      <button onClick={() => removeEducation(i)} className="p-1 text-red-400 h-fit"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                ) : (
                  <>
                    <h4 className="text-sm font-bold" style={{ color: 'var(--t-text-heading)' }}>{edu.degree}</h4>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--t-text-muted)' }}>{edu.institution}</p>
                  </>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6" style={{ color: 'var(--t-text-muted)' }}>
            <GraduationCap className="w-6 h-6 mx-auto mb-1.5 opacity-20" />
            <p className="text-xs">{editing ? 'Click "Add" to add your education' : 'No education added yet.'}</p>
          </div>
        )}
      </motion.div>

      {/* Domain Expertise */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
        className="nx-card p-6" data-testid="domain-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,132,31,0.12)' }}>
            <Star className="w-4 h-4" style={{ color: '#0D9488' }} />
          </div>
          <h3 className="text-lg font-bold" style={{ color: 'var(--t-text-heading)' }}>Domain Expertise</h3>
        </div>
        {editing && (
          <div className="flex items-center gap-2 mb-3">
            <Input value={newDomain} onChange={e => setNewDomain(e.target.value)} placeholder="e.g., Fintech, AI/ML..."
              className="flex-1 h-8 text-sm" style={{ background: 'var(--t-bg-input)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
              onKeyDown={e => { if (e.key === 'Enter') addDomain(); }} data-testid="new-domain-input" />
            <Button onClick={addDomain} className="h-8 px-3 text-[10px] font-bold rounded" data-testid="add-domain-btn"
              style={{ background: '#0D9488', color: '#fff' }}>
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>
        )}
        {(p.domain_expertise || []).length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {(p.domain_expertise || []).map((domain, i) => (
              <span key={i} className="text-xs py-1 px-3 rounded-lg font-medium flex items-center gap-1.5" data-testid={`domain-${i}`}
                style={{ background: 'var(--t-bg-section)', border: '1px solid var(--t-border)', color: 'var(--t-text-secondary)' }}>
                {domain}
                {editing && <button onClick={() => removeDomain(i)} className="text-red-400"><X className="w-3 h-3" /></button>}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-center py-4" style={{ color: 'var(--t-text-muted)' }}>
            <p className="text-xs">{editing ? 'Add your domain expertise areas' : 'No domains added yet.'}</p>
          </div>
        )}
      </motion.div>
    </div>
  );
}

function PField({ label, testId, value, onChange, icon: Icon, type = 'text' }) {
  return (
    <div>
      <label className="text-[10px] font-bold mb-0.5 block" style={{ color: 'var(--t-text-muted)' }}>{label}</label>
      <div className="relative">
        {Icon && <Icon className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--t-text-muted)' }} />}
        <Input data-testid={testId} type={type} value={value || ''} onChange={e => onChange(e.target.value)}
          className={`h-8 text-sm ${Icon ? 'pl-7' : ''}`}
          style={{ background: 'var(--t-bg-input)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }} />
      </div>
    </div>
  );
}

function InfoBit({ icon: Icon, text }) {
  if (!text) return null;
  return (
    <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--t-text-muted)' }}>
      <Icon className="w-3 h-3" style={{ color: '#0D9488' }} /> {text}
    </span>
  );
}
